import PageSkeleton from "@/components/page-skeleton";
import DialogSelectTeamPrompt from "@/components/team/dialog-select-team-prompt";
import { PinnedPromptsContextList } from "@/components/team/pinned-prompts-context-list";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToastError } from "@/components/ui/use-toast";
import { useTeams } from "@/contexts/teams-provider";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import { MAX_PINNED_PROMPTS } from "@/enums/pinned-prompts";
import { useApi } from "@/hooks/useApi";
import { usePinnedPromptsEditor } from "@/hooks/usePinnedPromptsEditor";
import { useTranslation } from "@/hooks/useTranslation";
import { getPromptId, getSupportedLang } from "@/lib/prompt-utils";
import { DialogDescription } from "@radix-ui/react-dialog";
import { Separator } from "@radix-ui/react-menubar";
import {
  MemberPinnedPromptDTO,
  PinnedContext,
  PromptDTO,
  TeamUserDTO,
} from "@shaarp/common";
import { PlusIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const CONTEXTS: PinnedContext[] = ["chat", "company"];

type PinOriginByContext = Record<PinnedContext, ReadonlySet<string>>;

const EMPTY_ORIGIN: PinOriginByContext = { chat: new Set(), company: new Set() };

/** Collects, per context, the ids of the pins that match `predicate`. */
const collectIds = (
  pins: Record<PinnedContext, MemberPinnedPromptDTO[]>,
  predicate: (pin: MemberPinnedPromptDTO) => boolean,
): PinOriginByContext => ({
  chat: new Set(pins.chat.filter(predicate).map((pin) => pin.promptId)),
  company: new Set(pins.company.filter(predicate).map((pin) => pin.promptId)),
});

export default function DataTableCellUserPinnedPrompts({
  user,
}: {
  user: TeamUserDTO;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { isAdmin, isManager, isAdvisor } = useTeams();

  // Same gate as AI instructions on this same table: billing users never had
  // pinned tactics to manage, and a plain member has no business reading or
  // changing a colleague's pins (criterion #12).
  const canEdit =
    user.role !== TeamUserRolesEnum.BILLING &&
    (isAdmin || isManager || isAdvisor);

  if (!canEdit) {
    return null;
  }

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        {t("components.dataTableUsers.pinnedPrompts.edit")}
      </Button>

      {open && (
        <DialogUserPinnedPromptsEdit
          user={user}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

const DialogUserPinnedPromptsEdit = ({
  user,
  onClose,
}: {
  user: TeamUserDTO;
  onClose: () => void;
}) => {
  const api = useApi();
  const toastError = useToastError();
  const { t, i18n } = useTranslation();
  const lang = getSupportedLang(i18n.language);

  const [pinnedIds, setPinnedIds] = useState<Record<PinnedContext, string[]>>({
    chat: [],
    company: [],
  });
  const [teamDefaultIds, setTeamDefaultIds] =
    useState<PinOriginByContext>(EMPTY_ORIGIN);
  const [personalIds, setPersonalIds] =
    useState<PinOriginByContext>(EMPTY_ORIGIN);
  const [prompts, setPrompts] = useState<PromptDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pickerContext, setPickerContext] = useState<PinnedContext | null>(
    null,
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [chatRes, companyRes, promptsRes] = await Promise.all([
        api.get<MemberPinnedPromptDTO[]>(
          `/my/team/user/${user.id}/pinned-prompts?context=chat`,
        ),
        api.get<MemberPinnedPromptDTO[]>(
          `/my/team/user/${user.id}/pinned-prompts?context=company`,
        ),
        api.get<PromptDTO[]>("/prompts"),
      ]);
      const pins = { chat: chatRes.data, company: companyRes.data };
      setPinnedIds({
        chat: pins.chat.map((pin) => pin.promptId),
        company: pins.company.map((pin) => pin.promptId),
      });
      setTeamDefaultIds(collectIds(pins, (pin) => pin.isTeamDefault));
      setPersonalIds(collectIds(pins, (pin) => pin.isPersonal));
      setPrompts(promptsRes.data);
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  }, [api, toastError, user.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const promptMap = new Map(prompts.map((p) => [getPromptId(p), p]));

  const { handleAdd, handleRemove, handleMove } = usePinnedPromptsEditor({
    state: pinnedIds,
    setState: setPinnedIds,
    refetch: fetchData,
    buildAddUrl: (context, promptId) =>
      `/my/team/user/${user.id}/pinned-prompts/${promptId}?context=${context}`,
    buildRemoveUrl: (context, promptId) =>
      `/my/team/user/${user.id}/pinned-prompts/${promptId}?context=${context}`,
    buildReorderUrl: () => `/my/team/user/${user.id}/pinned-prompts/reorder`,
  });

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[800px] w-full">
        <DialogHeader>
          <DialogTitle>
            {t("components.dataTableUsers.pinnedPrompts.dialogTitle", {
              fullName: user.fullName,
            })}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t("components.dataTableUsers.pinnedPrompts.onlyTeamNotice")}
          </DialogDescription>
        </DialogHeader>
        <Separator className="my-4" />
        {isLoading ? (
          <PageSkeleton />
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {CONTEXTS.map((context) => {
              const ids = pinnedIds[context];
              const max = MAX_PINNED_PROMPTS[context];
              const limitReached = ids.length >= max;
              const contextLabel = t(
                `components.dataTableUsers.pinnedPrompts.context${
                  context === "chat" ? "Chat" : "Company"
                }`,
              );

              return (
                <div key={context} className="space-y-3">
                  <PinnedPromptsContextList
                    contextLabel={contextLabel}
                    limitLabel={t(
                      "components.dataTableUsers.pinnedPrompts.limitLabel",
                      { count: ids.length, max },
                    )}
                    ids={ids}
                    promptMap={promptMap}
                    lang={lang}
                    emptyStateLabel={t(
                      "components.dataTableUsers.pinnedPrompts.empty",
                    )}
                    moveUpAria={t(
                      "components.dataTableUsers.pinnedPrompts.moveUpAria",
                    )}
                    moveDownAria={t(
                      "components.dataTableUsers.pinnedPrompts.moveDownAria",
                    )}
                    removeAria={t(
                      "components.dataTableUsers.pinnedPrompts.removeAria",
                    )}
                    onRemove={(id) => handleRemove(context, id)}
                    onMove={(index, direction) =>
                      handleMove(context, index, direction)
                    }
                    teamDefaultIds={teamDefaultIds[context]}
                    teamDefaultLabel={t(
                      "components.dataTableUsers.pinnedPrompts.teamDefaultBadge",
                    )}
                    lockedIds={personalIds[context]}
                    lockedLabel={t(
                      "components.dataTableUsers.pinnedPrompts.personalPin",
                    )}
                    lockedHint={t(
                      "components.dataTableUsers.pinnedPrompts.personalPinHint",
                    )}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={limitReached}
                    onClick={() => setPickerContext(context)}
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    {t("components.dataTableUsers.pinnedPrompts.addButton")}
                  </Button>
                  {limitReached && (
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "components.dataTableUsers.pinnedPrompts.limitReached",
                        { max },
                      )}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <Separator className="my-4" />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">
              {t("components.dataTableUsers.pinnedPrompts.close")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>

      {pickerContext && (
        <DialogSelectTeamPrompt
          title={t("components.dataTableUsers.pinnedPrompts.addButton")}
          excludeIds={pinnedIds[pickerContext]}
          onSelect={(prompt) => handleAdd(pickerContext, prompt)}
          onClose={() => setPickerContext(null)}
        />
      )}
    </Dialog>
  );
};
